"use client";

import { type Mutation, MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, splitLink, TRPCClientError, type TRPCLink } from "@trpc/client";
import type { AppRouter } from "@/server/api/root";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import superjson from "superjson";
import { ROUTES } from "@/constants/routes.constants";
import { SANDBOX_SESSION_HEADER } from "@/constants/sandboxSession.constants";
import { useSandboxSessionStore } from "@/stores/sandboxSessionStore";
import { STALE_SHORT, GC_TIME_SHORT } from "@/components/desktop/queryConfig.constants";
import { onPusherReconnect } from "@/lib/pusherClient";
import { XError } from "@/utils/error.utils";
import { reportClientError, reportClientWarning, type ClientReportTags } from "@/lib/clientReporting";
import { FREIGHT_DASHBOARD_QUERY_KEY, isFreightDashboardQueryKey, MAX_QUERIES_PER_BATCH, queryLaneOf, queryRetryDelay, shouldRefetchDashboardOnReconnect, shouldRetryDashboardQuery, shouldRetryQuery } from "./queryRetry.utils";
import { createReconnectRefetchScheduler } from "./reconnectRefetch.utils";
const RETIRED_QUERY_CACHE_DB = "wilson-query-cache";
if (typeof indexedDB !== "undefined") {
  indexedDB.deleteDatabase(RETIRED_QUERY_CACHE_DB);
}
function reportRefreshFailure(cause: Error | null): void {
  reportClientWarning({
    message: "authedBatchLink: token refresh failed",
    cause,
    tags: {
      surface: "trpc",
      action: "tokenRefresh"
    }
  });
}
async function refreshSession(): Promise<boolean> {
  try {
    const refreshResponse = await fetch("/api/auth/refresh", {
      method: "POST"
    });
    if (refreshResponse.ok) return true;
    if (refreshResponse.status !== 401) {
      reportRefreshFailure(new XError({
        message: "Token refresh refused",
        data: {
          status: refreshResponse.status
        }
      }));
    }
    return false;
  } catch (error) {
    reportRefreshFailure(error instanceof Error ? error : null);
    return false;
  }
}
function authedBatchLink() {
  return httpBatchLink({
    url: "/api/trpc",
    transformer: superjson,
    maxItems: MAX_QUERIES_PER_BATCH,
    headers: () => {
      const sandboxSessionId = useSandboxSessionStore.getState().sandboxSessionId;
      return sandboxSessionId ? {
        [SANDBOX_SESSION_HEADER]: sandboxSessionId
      } : {};
    },
    fetch: async (url, options) => {
      const response = await fetch(url, options);
      if (response.status !== 401) return response;
      const retried = (await refreshSession()) ? await fetch(url, options) : response;
      if (retried.status === 401) window.location.href = ROUTES.login;
      return retried;
    }
  });
}
export function createTrpcLinks(): TRPCLink<AppRouter>[] {
  return [splitLink({
    condition: op => queryLaneOf(op.path) === "slow",
    true: authedBatchLink(),
    false: splitLink({
      condition: op => queryLaneOf(op.path) === "shellPaint",
      true: authedBatchLink(),
      false: authedBatchLink()
    })
  })];
}
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      reportTags?: ClientReportTags;
    };
  }
}
function isTransportFailure(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return true;
  if (error.data == null) return true;
  return error.data.code == null;
}
export function handleMutationCacheError(error: unknown, _variables?: unknown, _onMutateResult?: unknown, mutation?: Mutation<unknown, unknown, unknown>): void {
  if (!isTransportFailure(error)) return;
  const path = error instanceof TRPCClientError && typeof error.data?.path === "string" ? error.data.path : undefined;
  reportClientError({
    message: "tRPC mutation failed",
    cause: error instanceof Error ? error : null,
    tags: mutation?.meta?.reportTags ?? {
      surface: "trpc",
      action: path ?? "mutation"
    }
  });
}
export default function TrpcProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [queryClient] = useState(() => {
    const client = new QueryClient({
      mutationCache: new MutationCache({
        onError: handleMutationCacheError
      }),
      defaultOptions: {
        queries: {
          staleTime: STALE_SHORT,
          gcTime: GC_TIME_SHORT,
          refetchOnWindowFocus: false,
          // boolean-naming-exempt: external contract — TanStack Query option
          refetchOnReconnect: true,
          // boolean-naming-exempt: external contract — TanStack Query option
          retry: shouldRetryQuery,
          retryDelay: queryRetryDelay
        }
      }
    });
    client.setQueryDefaults(FREIGHT_DASHBOARD_QUERY_KEY, {
      refetchOnReconnect: shouldRefetchDashboardOnReconnect,
      retry: shouldRetryDashboardQuery
    });
    return client;
  });
  const [trpcClient] = useState(() => {
    return api.createClient({
      links: createTrpcLinks()
    });
  });
  useEffect(() => {
    const scheduler = createReconnectRefetchScheduler({
      refetch: () => void queryClient.invalidateQueries({
        predicate: query => !isFreightDashboardQueryKey(query.queryKey)
      }, {
        cancelRefetch: false // boolean-naming-exempt: external contract — TanStack Query option
      })
    });
    const unsubscribe = onPusherReconnect(scheduler.onReconnect);
    return () => {
      unsubscribe();
      scheduler.dispose();
    };
  }, [queryClient]);
  useEffect(() => useSandboxSessionStore.subscribe((state, previous) => {
    if (state.sandboxSessionId !== previous.sandboxSessionId) {
      void queryClient.resetQueries();
    }
  }), [queryClient]);
  return <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-component="TrpcProvider" data-sentry-source-file="provider.tsx">
            <api.Provider client={trpcClient} queryClient={queryClient} data-sentry-element="api.Provider" data-sentry-source-file="provider.tsx">
                {children}
            </api.Provider>
        </QueryClientProvider>;
}